<template>
  <v-container fluid class="plans-wrapper">
    <div class="plans-header">
      <div>
        <h1 class="title-main">Planos da Conta</h1>
        <p class="subtitle-main">
          Seu cadastro ja comeca com 14 dias no plano completo. Depois disso, voce escolhe entre continuar no plano mensal ou contratar a versao premium personalizada.
        </p>
      </div>
    </div>

    <v-alert
      v-if="assinaturaAtual"
      :type="assinaturaAtual.acesso_liberado ? 'success' : 'warning'"
      outlined
      dense
      class="status-alert"
    >
      <div class="status-alert__content">
        <div>
          <div class="status-alert__title">
            {{ assinaturaAtual.plano ? assinaturaAtual.plano.nome : 'Plano da conta' }}
          </div>
          <div class="status-alert__text">
            {{ assinaturaAtual.resumo_status || 'Sem resumo disponivel.' }}
          </div>
          <div class="status-alert__meta" v-if="assinaturaAtual.fim">
            Vigencia ate {{ formatarData(assinaturaAtual.fim) }}
          </div>
        </div>

        <v-btn
          v-if="podeCancelarAssinatura"
          outlined
          color="error"
          class="status-alert__button"
          :loading="cancelando"
          @click="confirmarCancelamento"
        >
          Cancelar no fim do periodo
        </v-btn>
      </div>
    </v-alert>

    <v-alert
      v-else
      type="info"
      outlined
      dense
      class="status-alert"
    >
      Nenhuma assinatura encontrada para esta conta. O periodo gratuito e criado automaticamente no cadastro.
    </v-alert>

    <v-row justify="center" dense class="pl-8 pr-8">
      <v-col
        v-for="plano in planos"
        :key="plano.id"
        cols="12"
        md="4"
        class="d-flex justify-center"
      >
        <v-card class="plan-card" :class="getPlanoClass(plano)">
          <div v-if="plano.badge" class="plan-badge mt-3" :class="{ 'plan-badge--current': isPlanoAtual(plano) }">
            {{ plano.badge }}
          </div>

          <div class="plan-card__header">
            <div class="plan-card__name">{{ plano.nome }}</div>
            <div class="plan-card__price">{{ formatarValor(plano.valor) }}</div>
            <div class="plan-card__period">{{ formatarPeriodo(plano) }}</div>
            <p class="plan-card__description">{{ plano.descricao }}</p>
          </div>

          <v-divider class="my-4" />

          <div class="features">
            <div
              v-for="(item, index) in plano.itens"
              :key="`${plano.id}-${index}`"
              class="feature-item"
              :class="{ 'feature-item--blocked': item.bloqueado }"
            >
              <v-icon small class="mr-2" :color="item.bloqueado ? '#94a3b8' : '#12b57a'">
                {{ item.bloqueado ? 'mdi-close' : 'mdi-check' }}
              </v-icon>
              <span>{{ item.texto }}</span>
            </div>
          </div>

          <v-spacer />

          <v-card-actions class="pa-0 pt-6">
            <v-btn
              block
              depressed
              class="btn-plan"
              :class="{ 'btn-plan--premium': plano.id === 3 }"
              :loading="carregando && planoSelecionado === plano.id"
              :disabled="isBotaoDesabilitado(plano)"
              @click="assinar(plano)"
            >
              {{ getBotaoLabel(plano) }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="dialogCancelar" max-width="420">
      <v-card>
        <v-card-title class="headline">Cancelar assinatura</v-card-title>
        <v-card-text>
          O cancelamento sera aplicado no fim do periodo atual. Ate la, o acesso continua liberado normalmente.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogCancelar = false">Voltar</v-btn>
          <v-btn color="error" text :loading="cancelando" @click="cancelarAssinatura">
            Confirmar cancelamento
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import planosServices from './planosServices';

const PLANO_META = {
  1: {
    descricao: 'Mesmo pacote do plano de R$ 230, liberado por 14 dias para a transportadora testar o sistema completo.',
    itens: [
      { texto: '14 dias de uso completo', bloqueado: false },
      { texto: 'Cadastro de motoristas e veiculos', bloqueado: false },
      { texto: 'Controle de viagens', bloqueado: false },
      { texto: 'Controle financeiro', bloqueado: false },
      { texto: 'Acesso encerra apos 14 dias', bloqueado: true },
    ],
  },
  2: {
    descricao: 'Plano mensal com o mesmo pacote do free, mas sem limite de 14 dias.',
    destaque: true,
    itens: [
      { texto: 'Cadastro de motoristas e veiculos', bloqueado: false },
      { texto: 'Controle de viagens', bloqueado: false },
      { texto: 'Controle financeiro', bloqueado: false },
      { texto: 'Uso continuo sem limite de dias', bloqueado: false },
      { texto: 'Plano ideal para a rotina da transportadora', bloqueado: false },
    ],
  },
  3: {
    descricao: 'Tudo do plano de R$ 230 com recursos personalizados para a necessidade da transportadora.',
    premium: true,
    itens: [
      { texto: 'Tudo o que existe no plano de R$ 230', bloqueado: false },
      { texto: 'Campos personalizados', bloqueado: false },
      { texto: 'Ferramentas exclusivas', bloqueado: false },
      { texto: 'Relatorios personalizados', bloqueado: false },
      { texto: 'Ajustes para a operacao da empresa', bloqueado: false },
      { texto: 'Plano para quem precisa de algo sob medida', bloqueado: false },
    ],
  },
};

export default {
  name: 'indexPlanos',

  data() {
    return {
      carregandoPagina: false,
      carregando: false,
      cancelando: false,
      planoSelecionado: null,
      assinaturaAtual: null,
      dialogCancelar: false,
      planos: [],
    };
  },

  computed: {
    podeCancelarAssinatura() {
      return Boolean(
        this.assinaturaAtual &&
        this.assinaturaAtual.acesso_liberado &&
        this.assinaturaAtual.stripe_subscription_id &&
        !this.assinaturaAtual.cancelar_no_fim_periodo
      );
    },
  },

  async created() {
    await this.inicializarPagina();
  },

  methods: {
    getPlanoBadge(plano) {
      if (this.isPlanoAtual(plano)) {
        return this.assinaturaAtual?.acesso_liberado ? 'Plano atual' : 'Ultimo plano';
      }

      return plano.destaque ? 'Mais indicado' : '';
    },

    async inicializarPagina() {
      this.carregandoPagina = true;

      try {
        await this.tratarRetornoCheckout();
        await Promise.all([this.carregarPlanos(), this.carregarAssinaturaAtual()]);
      } finally {
        this.carregandoPagina = false;
      }
    },

    async carregarPlanos() {
      const response = await planosServices.listarPlanos();
      const planosApi = Array.isArray(response.data) ? response.data : [];

      this.planos = planosApi.map((plano) => {
        const meta = PLANO_META[Number(plano.id)] || {};
        const isAtual = this.assinaturaAtual && Number(this.assinaturaAtual.plano_id) === Number(plano.id);

        return {
          ...plano,
          descricao: meta.descricao || '',
          itens: Array.isArray(meta.itens) ? meta.itens : [],
          destaque: Boolean(meta.destaque),
          premium: Boolean(meta.premium),
          badge: isAtual && this.assinaturaAtual?.acesso_liberado
            ? 'Plano atual'
            : (isAtual ? 'Ultimo plano' : (meta.destaque ? 'Mais indicado' : '')),
        };
      });
    },

    async carregarAssinaturaAtual() {
      try {
        const response = await planosServices.buscarAssinaturaAtual();
        this.assinaturaAtual = response.data || null;
      } catch {
        this.assinaturaAtual = null;
      }
    },

    async tratarRetornoCheckout() {
      const { status, session_id } = this.$route.query;

      if (status === 'sucesso' && session_id) {
        try {
          await planosServices.confirmarAssinatura(session_id);
          this.$toasted && this.$toasted.show('Assinatura confirmada com sucesso.', { type: 'success' });
        } catch (error) {
          const msg = error?.response?.data?.error || 'Nao foi possivel confirmar a assinatura.';
          this.$toasted && this.$toasted.show(msg, { type: 'error' });
        } finally {
          this.$router.replace({ path: '/planos' });
        }

        return;
      }

      if (status === 'cancelado') {
        this.$toasted && this.$toasted.show('Pagamento cancelado.', { type: 'info' });
        this.$router.replace({ path: '/planos' });
      }
    },

    isPlanoAtual(plano) {
      return Boolean(
        this.assinaturaAtual &&
        Number(this.assinaturaAtual.plano_id) === Number(plano.id)
      );
    },

    isBotaoDesabilitado(plano) {
      if (this.carregando) return true;
      if (this.isPlanoAtual(plano) && this.assinaturaAtual?.acesso_liberado) return true;
      if (Number(plano.valor) === 0) return true;

      return false;
    },

    getBotaoLabel(plano) {
      if (this.isPlanoAtual(plano)) {
        if (this.assinaturaAtual?.cancelar_no_fim_periodo) {
          return 'Cancelamento agendado';
        }

        if (this.assinaturaAtual?.acesso_liberado) {
          return 'Plano atual';
        }

        return Number(plano.valor || 0) === 0 ? 'Trial encerrado' : 'Reativar este plano';
      }

      if (Number(plano.valor) === 0) {
        return 'Trial do cadastro';
      }

      return this.assinaturaAtual?.acesso_liberado ? 'Trocar para este plano' : 'Assinar plano';
    },

    getPlanoClass(plano) {
      return {
        'plan-card--featured': plano.destaque,
        'plan-card--premium': plano.premium,
        'plan-card--current': this.isPlanoAtual(plano) && this.assinaturaAtual?.acesso_liberado,
        'plan-card--inactive': this.isPlanoAtual(plano) && !this.assinaturaAtual?.acesso_liberado,
      };
    },

    formatarValor(valor) {
      return Number(valor || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 0,
      });
    },

    formatarPeriodo(plano) {
      if (Number(plano.valor || 0) === 0) {
        return `${plano.duracao_dias} dias gratuitos`;
      }

      return 'mensal';
    },

    formatarData(data) {
      if (!data) return '-';

      const parsed = new Date(data);
      if (Number.isNaN(parsed.getTime())) return '-';

      return parsed.toLocaleDateString('pt-BR');
    },

    async assinar(plano) {
      if (Number(plano.valor || 0) === 0) return;

      this.carregando = true;
      this.planoSelecionado = plano.id;

      try {
        const response = await planosServices.criarSessaoCheckout(plano.id);

        if (response?.data?.url) {
          window.location.href = response.data.url;
          return;
        }

        this.$toasted && this.$toasted.show('Nao foi possivel gerar o link de pagamento.', { type: 'error' });
      } catch (error) {
        const msg = error?.response?.data?.error || 'Erro ao iniciar a assinatura.';
        this.$toasted && this.$toasted.show(msg, { type: 'error' });
      } finally {
        this.carregando = false;
        this.planoSelecionado = null;
      }
    },

    confirmarCancelamento() {
      this.dialogCancelar = true;
    },

    async cancelarAssinatura() {
      this.cancelando = true;

      try {
        const response = await planosServices.cancelarAssinatura();
        this.assinaturaAtual = response?.data?.assinatura || this.assinaturaAtual;
        this.dialogCancelar = false;
        this.$toasted && this.$toasted.show('Cancelamento agendado para o fim do periodo.', { type: 'success' });
      } catch (error) {
        const msg = error?.response?.data?.error || 'Erro ao cancelar assinatura.';
        this.$toasted && this.$toasted.show(msg, { type: 'error' });
      } finally {
        this.cancelando = false;
      }
    },
  },

  watch: {
    assinaturaAtual() {
      if (this.planos.length) {
        this.planos = this.planos.map((plano) => ({
          ...plano,
          badge: this.getPlanoBadge(plano),
        }));
      }
    },
  },
};
</script>

<style scoped>
.plans-wrapper {
  min-height: 100vh;
  padding: 32px 20px 40px;
  background: linear-gradient(180deg, #f8fbfa 0%, #eef6f3 100%);
}

.plans-header {
  max-width: 880px;
  margin: 0 auto 24px;
  text-align: center;
}

.title-main {
  font-size: 30px;
  line-height: 1.15;
  font-weight: 800;
  color: #18352c;
}

.subtitle-main {
  margin-top: 10px;
  font-size: 15px;
  color: #587267;
}

.status-alert {
  max-width: 960px;
  margin: 0 auto 24px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.9);
}

.status-alert__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.status-alert__title {
  font-size: 16px;
  font-weight: 700;
  color: #18352c;
}

.status-alert__text,
.status-alert__meta {
  font-size: 13px;
  color: #5f6f68;
}

.status-alert__button {
  white-space: nowrap;
}

.plan-card {
  position: relative;
  width: 100%;
  max-width: 320px;
  padding: 24px;
  border-radius: 20px;
  border: 1px solid #d9e6e0;
  background: #ffffff;
  box-shadow: 0 18px 40px rgba(16, 58, 44, 0.06);
  display: flex;
  flex-direction: column;
}

.plan-card--featured {
  border-color: #0f8d61;
}

.plan-card--premium {
  border-color: #0d6f92;
}

.plan-card--current {
  box-shadow: 0 20px 42px rgba(16, 181, 122, 0.16);
  border-color: #12b57a;
}

.plan-card--inactive {
  border-color: #f59e0b;
}

.plan-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  background: #0f8d61;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.plan-badge--current {
  background: #12b57a;
}

.plan-card__header {
  min-height: 148px;
}

.plan-card__name {
  font-size: 20px;
  font-weight: 700;
  color: #18352c;
}

.plan-card__price {
  margin-top: 10px;
  font-size: 34px;
  font-weight: 800;
  color: #0c4f39;
}

.plan-card__period {
  margin-top: 2px;
  font-size: 13px;
  color: #5f6f68;
  text-transform: lowercase;
}

.plan-card__description {
  margin-top: 14px;
  font-size: 14px;
  line-height: 1.5;
  color: #587267;
}

.features {
  display: grid;
  gap: 10px;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  font-size: 14px;
  color: #28473d;
}

.feature-item--blocked {
  color: #94a3b8;
}

.btn-plan {
  height: 42px !important;
  border-radius: 12px !important;
  background: #12b57a !important;
  color: #fff !important;
  font-weight: 700;
  letter-spacing: 0;
}

.btn-plan--premium {
  background: #0d6f92 !important;
}

@media (max-width: 960px) {
  .plan-card {
    max-width: none;
    margin-bottom: 16px;
  }
}
</style>